import React, {
    ChangeEvent,
    Dispatch,
    FC,
    SetStateAction,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useStyles } from './styles';
import placeholder1 from '../../../../assets/img/gotmy-placeholder-1.jpeg';
import placeholder2 from '../../../../assets/img/gotmy-placeholder-2.jpeg';
import placeholder3 from '../../../../assets/img/gotmy-placeholder-3.jpeg';
import pickThumbIcon from '../../../../assets/img/icons/ic-photo-white.svg';
import ImageCropper from './parts/ImageCropper';
import ThumbnailPicker from './parts/ThumbnailPicker';

interface Props {
    onCoverImageSave: Dispatch<SetStateAction<string>>;
}

const CoverImage: FC<Props> = ({ onCoverImageSave }) => {
    const classes = useStyles();

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [coverImage, setCoverImage] = useState<string>(placeholder1);
    const [isThumbnailModalOpen, setIsThumbnailModalOpen] = useState<boolean>(false);
    const [isCropperModalOpen, setIsCropperModalOpen] = useState<boolean>(false);

    useEffect(() => {
        onCoverImageSave(coverImage);
    }, [coverImage, onCoverImageSave]);

    const handleCoverImageButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleThumbnailModalOpen = () => setIsThumbnailModalOpen(true);
    const handleThumbnailModalClose = () => setIsThumbnailModalOpen(false);

    const handleCropperModalOpen = () => setIsCropperModalOpen(true);
    const handleCropperModalClose = () => setIsCropperModalOpen(false);
    const handleImageChange = (image: string) => {
        setCoverImage(image);
        handleCropperModalClose();
    };

    const handleThumbnailPicked = (image: string) => {
        setCoverImage(image);
        handleThumbnailModalClose();
    };

    const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList) return;

        const reader = new FileReader();
        reader.addEventListener('load', () => setCoverImage(reader.result?.toString() || ''));
        reader.readAsDataURL(fileList[0]);

        handleCropperModalOpen();
    };

    const images = useMemo(() => [placeholder1, placeholder2, placeholder3], []);

    return (
        <>
            <Typography className={classes.title}>Cover Image</Typography>
            <Typography className={classes.subtitle}>
                This image will be shown as cover of your Live Event
            </Typography>

            <Box onClick={handleCoverImageButtonClick} className={classes.coverImageContainer}>
                <img src={coverImage} alt="" className={classes.coverImage} />
                <Button className={classes.pickThumbButton}>
                    <img alt="" src={pickThumbIcon} className={classes.pickThumbIcon} />
                </Button>

                <input
                    type="file"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                />
            </Box>

            <Typography className={classes.title}>Would you rather use one of our thumbnails?</Typography>

            <Box className={classes.selectThumbContainer}>
                <Button className={classes.selectThumbButton} onClick={handleThumbnailModalOpen}>
                    Select thumbnails
                </Button>
            </Box>

            <ThumbnailPicker
                images={images}
                isOpen={isThumbnailModalOpen}
                onClose={handleThumbnailModalClose}
                onThumbnailPick={handleThumbnailPicked}
            />

            <ImageCropper
                image={coverImage}
                isOpen={isCropperModalOpen}
                onClose={handleCropperModalClose}
                handleImageChange={handleImageChange}
                changeFile={handleCoverImageButtonClick}
            />
        </>
    );
};

export default CoverImage;
